/**
 * NetClient - 실시간 서버(WebSocket) 연결
 * - 월드 입장 시 connect(), 메인메뉴 복귀 시 disconnect()
 * - 끊기면 자동 재접속 (재접속 때는 현재 상태로 hello 를 다시 보냄)
 * - 같은 이름으로 다른 곳에서 접속하면 이쪽 연결은 종료된다 (kicked)
 */

import { PartyMsg, PartyTransport } from '../modules/party/PartyManager';

export interface NetPlayerInfo {
  id: number;
  name: string;
  job: string;
  role: string;
  level: number;
  guild?: string;
  appearance: any;
  zoneId: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
}

export type NetHello = Omit<NetPlayerInfo, 'id'>;

export class NetClient {
  private ws: WebSocket | null = null;
  private shouldRun = false;
  private retry = 0;
  private retryTimer: number | null = null;
  private helloProvider: (() => NetHello | null) | null = null;
  private moveAcc = 0;
  private last = '';

  public onPlayers?: (list: NetPlayerInfo[]) => void;
  public onJoin?: (p: NetPlayerInfo) => void;
  public onLeave?: (id: number) => void;
  public onMove?: (id: number, x: number, y: number, hp: number, maxHp: number) => void;
  public onParty?: (msg: PartyMsg) => void;
  public onChat?: (msg: { channel: string; from: string; text: string; to?: string; guild?: string; zoneId?: number; x?: number; y?: number }) => void;
  public onMobHit?: (msg: { mobId: string; damage: number; remainingHp: number; attackerName: string }) => void;
  public onMobDead?: (msg: { mobId: string; killerName: string }) => void;
  public onStatus?: (text: string) => void;
  public onKicked?: (reason: string) => void;
  public onVersion?: (serverVersion: string) => void;
  /** 연결이 끊기거나 종료될 때 (원격 플레이어 정리용) */
  public onReset?: () => void;

  public isOpen(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  public connect(helloProvider: () => NetHello | null): void {
    this.helloProvider = helloProvider;
    this.shouldRun = true;
    this.retry = 0;
    this.last = '';
    this.open();
  }

  public disconnect(): void {
    this.shouldRun = false;
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.onReset?.();
  }

  private url(): string {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}/ws`;
  }

  private open(): void {
    if (!this.shouldRun) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      const hello = this.helloProvider?.();
      if (hello) ws.send(JSON.stringify({ t: 'hello', ...hello }));
      this.last = '';
    };
    ws.onmessage = (e) => {
      let m: any;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (m.t) {
        case 'welcome':
          if (m.version) this.onVersion?.(m.version);
          break;
        case 'version':
          if (m.version) this.onVersion?.(m.version);
          break;
        case 'players':
          this.onPlayers?.(m.list || []);
          break;
        case 'join':
          this.onJoin?.(m.p);
          break;
        case 'leave':
          this.onLeave?.(m.id);
          break;
        case 'move':
          this.onMove?.(m.id, m.x, m.y, m.hp, m.maxHp);
          break;
        case 'party':
          this.onParty?.(m.msg);
          break;
        case 'chat':
          this.onChat?.(m);
          break;
        case 'mob_hit':
          this.onMobHit?.(m);
          break;
        case 'mob_dead':
          this.onMobDead?.(m);
          break;
        case 'kicked':
          this.shouldRun = false;
          this.onStatus?.('다른 곳에서 같은 캐릭터로 접속하여 이 연결이 종료되었습니다.');
          this.onKicked?.(m.reason || 'duplicate');
          break;
      }
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.onReset?.();
      if (this.shouldRun) this.scheduleRetry();
    };
    ws.onerror = () => {
      /* onclose 에서 재시도 */
    };
  }

  private scheduleRetry(): void {
    if (!this.shouldRun || this.retryTimer !== null) return;
    const delay = Math.min(5000, 1000 * Math.pow(1.6, this.retry++));
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  public send(obj: unknown): void {
    if (this.isOpen()) this.ws!.send(JSON.stringify(obj));
  }

  public sendChat(
    channel: 'normal' | 'party' | 'guild' | 'broadcast' | 'world' | 'whisper' | 'all',
    text: string,
    to?: string,
    members?: string[],
    guild?: string
  ): void {
    this.send({ t: 'chat', channel, text, to, members, guild });
  }

  public sendMobHit(mobId: string, damage: number, remainingHp: number): void {
    this.send({ t: 'mob_hit', mobId, damage, remainingHp });
  }

  public sendMobDead(mobId: string): void {
    this.send({ t: 'mob_dead', mobId });
  }

  /** 매 프레임: 내 위치/체력이 바뀌었으면 0.1초 간격으로 전송 */
  public tick(dt: number, me: { x: number; y: number; level?: number; resources: { hp: number; maxHp: number } }, zoneId: number): void {
    if (!this.isOpen()) return;
    this.moveAcc += dt;
    if (this.moveAcc < 0.1) return;
    this.moveAcc = 0;
    const hp = Math.round(me.resources.hp);
    const maxHp = me.resources.maxHp;
    const key = `${zoneId}|${me.x.toFixed(2)}|${me.y.toFixed(2)}|${hp}|${maxHp}|${me.level ?? 1}`;
    if (key === this.last) return;
    this.last = key;
    this.send({ t: 'move', zoneId, x: +me.x.toFixed(2), y: +me.y.toFixed(2), hp, maxHp, level: me.level ?? 1 });
  }
}

/** 파티 메시지를 서버를 통해 주고받는 전송 계층 */
export class NetPartyTransport implements PartyTransport {
  private cb: ((msg: PartyMsg) => void) | null = null;
  constructor(private net: NetClient) {
    net.onParty = (m) => this.cb?.(m);
  }
  send(msg: PartyMsg): void {
    this.net.send({ t: 'party', msg });
  }
  onMessage(cb: (msg: PartyMsg) => void): void {
    this.cb = cb;
  }
}
